import { useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft, ShieldAlert } from 'lucide-react'

import { savingsAccountQueryOptions } from '@/api/savings-api'
import { useBranchSelection } from '@/components/app-shell/use-branch-selection'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { BalanceSummary } from '@/features/savings/account-panels'
import {
  DepositForm,
  SecurityDepositForm,
  TransferForm,
  WithdrawalForm,
} from '@/features/savings/cash-forms'
import { productClassLabel } from '@/features/savings/constants'
import { savingsPaths, useSavingsNavigate } from '@/features/savings/navigation'
import { SAVINGS_PERMISSIONS, useSavingsPermissions } from '@/features/savings/permissions'

import { savingsCashSearchSchema, type SavingsCashTab } from './search'

const PANEL_ID = 'savings-cash-panel'

/**
 * The teller screen: deposits, withdrawals, transfers, and security-deposit top-ups.
 *
 * **It refuses below `lg` rather than degrading**, and it is the case `docs/mobile-policy.md` was
 * written around. Cash is at a counter with a till, a drawer, and a receipt printer; the constraint
 * is physical rather than visual, and somebody posting a deposit from a phone is not standing where
 * the money is. Keying an amount is also the single thing touch is worst at, and a mis-keyed
 * amount here is a discrepancy against real money, found at end-of-day reconciliation.
 *
 * Which form is on screen is in the URL. A teller sent here from an account should land on the
 * right tab, and a reload mid-shift must not quietly move them from a security-deposit top-up to an
 * ordinary deposit.
 */
export function SavingsCashPage() {
  const params: Record<string, string | undefined> = useParams({ strict: false })
  const accountId = params.accountId ?? ''
  const navigate = useSavingsNavigate()
  const permissions = useSavingsPermissions()
  const { selectedBranchId } = useBranchSelection()

  const search = savingsCashSearchSchema.parse(useSearch({ strict: false }))
  const query = useQuery(savingsAccountQueryOptions(accountId))

  // The four forms, reduced to the ones this role could possibly submit. A tab the operator can
  // never post through is not a discovery aid — it is a dead end they will find by tapping it.
  const availableTabs: TabDefinition<SavingsCashTab>[] = [
    ...(permissions.canPostDeposit
      ? [{ id: 'deposit' as const, label: 'Deposit' }]
      : []),
    ...(permissions.canPostWithdrawal
      ? [{ id: 'withdrawal' as const, label: 'Withdrawal' }]
      : []),
    ...(permissions.canPostTransfer ? [{ id: 'transfer' as const, label: 'Transfer' }] : []),
    ...(permissions.canTopUpSecurityDeposit
      ? [{ id: 'security-deposit' as const, label: 'Security deposit' }]
      : []),
  ]

  const requestedTab = search.tab
  const tab: SavingsCashTab =
    requestedTab && availableTabs.some((candidate) => candidate.id === requestedTab)
      ? requestedTab
      : (availableTabs[0]?.id ?? 'deposit')

  const selectTab = (next: SavingsCashTab) => {
    navigate(savingsPaths.accountCash(accountId), { search: { tab: next }, replace: true })
  }

  return (
    <RequiresWiderScreen
      screen="Posting a savings transaction"
      reason="Cash is counted at a counter, beside a till and a drawer, and an amount keyed with a thumb is the mistake that shows up at end-of-day reconciliation. Balances and statements are readable at any width — it is the posting that needs the desk."
      backTo="/savings"
      backLabel="Back to savings"
    >
      <button
        type="button"
        onClick={() => navigate(savingsPaths.account(accountId))}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to the account
      </button>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This account could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={query.data.customerName}
            reference={query.data.accountNumber}
            description="Every figure below is read from the server. Nothing on this screen is calculated in the browser."
            badges={
              <>
                <StatusBadge status={query.data.status} />
                <Badge tone={query.data.productClass === 'SecurityDeposit' ? 'warning' : 'neutral'}>
                  {productClassLabel(query.data.productClass)}
                </Badge>
              </>
            }
          />

          <BalanceSummary account={query.data} />

          {query.data.status !== 'Active' ? (
            <Alert tone="danger">
              <ShieldAlert aria-hidden />
              <div className="space-y-1">
                <AlertTitle>This account is {query.data.status.toLowerCase()}</AlertTitle>
                <AlertDescription>
                  {query.data.status === 'Dormant'
                    ? 'A dormant account refuses debits until it is reactivated. Money can still be paid in.'
                    : query.data.status === 'Frozen'
                      ? 'A frozen account refuses everything. A supervisor has to release it.'
                      : 'A closed account takes no further postings.'}
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          {availableTabs.length === 0 ? (
            <Alert tone="warning">
              <ShieldAlert aria-hidden />
              <div className="space-y-1">
                <AlertTitle>Your role posts none of these</AlertTitle>
                <AlertDescription>
                  Posting needs one of{' '}
                  <span className="font-mono text-xs">{SAVINGS_PERMISSIONS.depositPost}</span>,{' '}
                  <span className="font-mono text-xs">{SAVINGS_PERMISSIONS.withdrawalPost}</span>,{' '}
                  <span className="font-mono text-xs">{SAVINGS_PERMISSIONS.transferPost}</span>, or{' '}
                  <span className="font-mono text-xs">
                    {SAVINGS_PERMISSIONS.securityDepositTopUp}
                  </span>
                  . The balances above are what this role is served.
                </AlertDescription>
              </div>
            </Alert>
          ) : (
            <div className="space-y-4">
              <TabStrip<SavingsCashTab>
                tabs={availableTabs}
                active={tab}
                onChange={selectTab}
                panelId={PANEL_ID}
                label="Savings postings"
              />

              <Card>
                <CardContent>
                  <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
                    {/*
                      Each form is keyed by the account as well as the tab, so moving between two
                      accounts can never leave a half-typed amount from the first one sitting in the
                      field for the second. React would otherwise reuse the component and its state.
                    */}
                    {tab === 'deposit' ? (
                      <DepositForm
                        key={`deposit-${query.data.id}`}
                        account={query.data}
                        branchId={selectedBranchId}
                      />
                    ) : null}
                    {tab === 'withdrawal' ? (
                      <WithdrawalForm
                        key={`withdrawal-${query.data.id}`}
                        account={query.data}
                        branchId={selectedBranchId}
                      />
                    ) : null}
                    {tab === 'transfer' ? (
                      <TransferForm
                        key={`transfer-${query.data.id}`}
                        account={query.data}
                        branchId={selectedBranchId}
                      />
                    ) : null}
                    {tab === 'security-deposit' ? (
                      <SecurityDepositForm
                        key={`security-deposit-${query.data.id}`}
                        account={query.data}
                        branchId={selectedBranchId}
                      />
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </RequiresWiderScreen>
  )
}
